// ============= UTILITY FUNCTIONS FOR FILE HANDLING =============

// Icon imports
import docIcon from "../../../assets/icons/doc-icon.svg";
import jpgIcon from "../../../assets/icons/jpg-icon.svg";
import pdfIcon from "../../../assets/icons/pdf-icon.svg";
import pngIcon from "../../../assets/icons/png-icon.svg";
import pptIcon from "../../../assets/icons/ppt-icon.svg";
import xlsxIcon from "../../../assets/icons/xlsx-icon.svg";
import mp4Icon from "../../../assets/icons/mp4-icon.svg";
import othIcon from "../../../assets/icons/oth.png";

/**
 * Format file size from bytes to KB or MB
 * @param {number} sizeInBytes - Size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (sizeInBytes) => {
  if (!sizeInBytes) return "";
  const sizeInKB = sizeInBytes / 1024;
  const sizeInMB = sizeInKB / 1024;
  return sizeInMB >= 1
    ? `${sizeInMB.toFixed(2)} MB`
    : `${sizeInKB.toFixed(2)} KB`;
};

/**
 * Normalize file extension to standard types
 * @param {string} extension - File extension
 * @returns {string} Normalized file type
 */
export const normalizeFileType = (extension) => {
  if (!extension) return "";
  extension = extension.toLowerCase();

  const fileTypeMap = {
    ppt: ["ppt", "pptx", "powerpoint"],
    word: ["doc", "docx", "word"],
    pdf: ["pdf"],
    xlsx: ["xls", "xlsx", "excel", "csv"],
    jpg: ["jpg", "jpeg", "image", "images"],
    png: ["png"],
    gif: ["gif"],
    webp: ["webp"],
    svg: ["svg"],
    mp4: ["mp4", "mpeg4"],
    video: ["video", "videos", "avi", "mov", "wmv", "mkv", "webm"],
    audio: ["mp3", "wav", "ogg", "m4a", "flac"],
    archive: ["zip", "rar", "7z", "tar", "gz"],
    code: ["js", "jsx", "ts", "tsx", "html", "css", "json", "xml", "py", "java", "c", "cpp", "php"],
    text: ["txt", "md", "rtf"],
  };

  for (const [normalizedType, extensions] of Object.entries(fileTypeMap)) {
    if (extensions.includes(extension)) {
      return normalizedType;
    }
  }

  return extension;
};

/**
 * Get accepted file types for file input
 * Supports related extensions (e.g., .doc and .docx for word files)
 * @param {string} fileType - File type from file_requirements
 * @param {Array} fileArray - Array of file requirements
 * @returns {string} Accepted file types in format ".ext1,.ext2"
 */
export const getAcceptedFileTypes = (fileType, fileArray) => {
  if (!fileType || !fileArray) return "*/*";

  // Find the file type in file_requirements
  const fileReq = fileArray.find((req) => req.file_type === fileType);

  if (fileReq && fileReq.extension) {
    // Split extensions and expand each to include related formats
    const allExtensions = new Set();
    
    fileReq.extension.split(",").forEach((ext) => {
      const trimmedExt = ext.trim().toLowerCase();
      
      // Normalize the extension to get its type
      const normalizedType = normalizeFileType(trimmedExt);
      
      // Based on normalized type, add all related extensions
      switch (normalizedType) {
        case "pdf":
          allExtensions.add(".pdf");
          break;
        case "ppt":
          allExtensions.add(".ppt");
          allExtensions.add(".pptx");
          break;
        case "word":
          allExtensions.add(".doc");
          allExtensions.add(".docx");
          break;
        case "jpg":
          allExtensions.add(".jpg");
          allExtensions.add(".jpeg");
          break;
        case "png":
          allExtensions.add(".png");
          break;
        case "gif":
          allExtensions.add(".gif");
          break;
        case "webp":
          allExtensions.add(".webp");
          break;
        case "svg":
          allExtensions.add(".svg");
          break;
        case "xlsx":
          allExtensions.add(".xlsx");
          allExtensions.add(".xls");
          allExtensions.add(".csv");
          break;
        case "mp4":
          allExtensions.add(".mp4");
          break;
        case "video":
          allExtensions.add(".mp4");
          allExtensions.add(".avi");
          allExtensions.add(".mov");
          allExtensions.add(".wmv");
          allExtensions.add(".mkv");
          allExtensions.add(".webm");
          allExtensions.add(".flv");
          break;
        default:
          // If no mapping found, just add the original extension with dot
          allExtensions.add(`.${trimmedExt}`);
      }
    });
    
    return Array.from(allExtensions).join(",");
  }

  // Fallback to normalized file type matching for standard types
  const normalizedType = normalizeFileType(fileType);

  switch (normalizedType) {
    case "pdf":
      return ".pdf";
    case "ppt":
      return ".ppt,.pptx";
    case "word":
      return ".doc,.docx";
    case "jpg":
      return ".jpg,.jpeg";
    case "png":
      return ".png";
    case "gif":
      return ".gif";
    case "webp":
      return ".webp";
    case "svg":
      return ".svg";
    case "image":
      return ".jpg,.jpeg,.png,.gif,.webp,.svg,.bmp";
    case "xlsx":
      return ".xlsx,.xls,.csv";
    case "mp4":
      return ".mp4";
    case "video":
      return ".mp4,.avi,.mov,.wmv,.mkv,.webm,.flv";
    default:
      return "*/*";
  }
};

/**
 * Get file icon based on extension
 * @param {string} extension - File extension
 * @returns {string} Icon path
 */
export const getFileIcon = (extension) => {
  if (!extension) return othIcon;

  const normalizedExt = normalizeFileType(extension);

  switch (normalizedExt) {
    case "pdf":
      return pdfIcon;
    case "ppt":
      return pptIcon;
    case "word":
      return docIcon;
    case "jpg":
      return jpgIcon;
    case "png":
      return pngIcon;
    case "gif":
      return pngIcon;
    case "webp":
      return pngIcon;
    case "svg":
      return pngIcon;
    case "image":
      return jpgIcon;
    case "xlsx":
      return xlsxIcon;
    case "mp4":
      return mp4Icon;
    case "video":
      return mp4Icon;
    default:
      return othIcon;
  }
};

/**
 * Format date string to readable format
 * @param {string} dateString - Date string
 * @returns {string} Formatted date string
 */
export function timeFormatter(dateString) {
  const date = new Date(dateString);
  const formattedTime = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return formattedTime;
}

export const getExtensionTextColor = (fileType) => {
  const normalized = normalizeFileType(fileType);

  switch (normalized) {
    case "pdf":
      return "#DF0000"; // Red
    case "word":
      return "#007BFF"; // Blue
    case "ppt":
      return "#F29900"; // Orange
    case "xlsx":
      return "#09C425"; // Green
    case "jpg":
    case "png":
    case "gif":
    case "webp":
    case "svg":
    case "image":
      return "#9C27B0"; // Purple (Changed to distinguish from Word)
    case "mp4":
    case "video":
      return "#E91E63"; // Pink
    case "audio":
      return "#E91E63"; // Pink
    case "archive":
      return "#795548"; // Brown
    case "code":
      return "#607D8B"; // Blue Grey
    case "text":
      return "#616161"; // Grey
    default:
      return "#007BFF"; // Default Blue
  }
};